/**
 * RecommendedActionsPanel Component
 *
 * Displays aggregated recommended actions from repository assessments.
 * Groups actions by priority and provides quick access to trigger workflows.
 *
 * Used in adaptive dashboard for mature clients.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useCallback } from 'react';
import { Card, Badge, Button, Spinner, Tooltip } from 'flowbite-react';
import {
  HiLightBulb,
  HiShieldCheck,
  HiDocumentSearch,
  HiLightningBolt,
  HiPlay,
  HiChevronDown,
  HiChevronUp,
  HiExternalLink,
  HiRefresh,
  HiClock,
  HiCheckCircle,
} from 'react-icons/hi';
import { FaGithub } from 'react-icons/fa';

// =============================================================================
// TYPES
// =============================================================================

export interface RecommendedAction {
  action_id: string;
  type: string;
  label: string;
  priority: 'high' | 'medium' | 'low';
  workflow: string;
  description: string;
  repository?: string;
  assessment_id?: string;
  estimated_time_minutes?: number;
  potential_impact?: string;
}

export interface GroupedActions {
  high: RecommendedAction[];
  medium: RecommendedAction[];
  low: RecommendedAction[];
}

interface RecommendedActionsPanelProps {
  actions: RecommendedAction[];
  onTriggerAction: (action: RecommendedAction) => void;
  onDismissAction?: (actionId: string) => void;
  onRefresh?: () => void;
  loading?: boolean;
  maxVisible?: number;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

const getPriorityColor = (priority: 'high' | 'medium' | 'low'): 'failure' | 'warning' | 'info' => {
  switch (priority) {
    case 'high':
      return 'failure';
    case 'medium':
      return 'warning';
    case 'low':
      return 'info';
  }
};

const getPriorityLabel = (priority: 'high' | 'medium' | 'low'): string => {
  switch (priority) {
    case 'high':
      return 'High Priority';
    case 'medium':
      return 'Medium Priority';
    case 'low':
      return 'Low Priority';
  }
};

const getWorkflowIcon = (workflow: string): React.ReactNode => {
  switch (workflow) {
    case 'security-review':
      return <HiShieldCheck className="w-4 h-4" />;
    case 'quality-check':
      return <HiDocumentSearch className="w-4 h-4" />;
    case 'deploy-feature':
      return <HiLightningBolt className="w-4 h-4" />;
    default:
      return <HiPlay className="w-4 h-4" />;
  }
};

const groupActionsByPriority = (actions: RecommendedAction[]): GroupedActions => {
  return actions.reduce<GroupedActions>(
    (acc, action) => {
      acc[action.priority].push(action);
      return acc;
    },
    { high: [], medium: [], low: [] }
  );
};

// =============================================================================
// COMPONENT
// =============================================================================

export const RecommendedActionsPanel: React.FC<RecommendedActionsPanelProps> = ({
  actions,
  onTriggerAction,
  onDismissAction,
  onRefresh,
  loading = false,
  maxVisible = 10,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [processingActions, setProcessingActions] = useState<Set<string>>(new Set());

  const groupedActions = groupActionsByPriority(actions);
  const totalActions = actions.length;
  const highPriorityCount = groupedActions.high.length;

  // Handle action trigger with loading state
  const handleTriggerAction = useCallback(async (action: RecommendedAction) => {
    setProcessingActions(prev => new Set(prev).add(action.action_id));
    try {
      await onTriggerAction(action);
    } finally {
      setProcessingActions(prev => {
        const next = new Set(prev);
        next.delete(action.action_id);
        return next;
      });
    }
  }, [onTriggerAction]);

  // Determine which actions to show
  const visibleActions = expanded
    ? actions
    : actions.slice(0, maxVisible);

  // Render individual action item
  const renderActionItem = (action: RecommendedAction) => {
    const isProcessing = processingActions.has(action.action_id);

    return (
      <div
        key={action.action_id}
        className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
      >
        {/* Workflow Icon */}
        <div className={`flex-shrink-0 p-2 rounded-lg ${
          action.priority === 'high' ? 'bg-red-100 text-red-600' :
          action.priority === 'medium' ? 'bg-yellow-100 text-yellow-600' : 'bg-blue-100 text-blue-600'
        }`}>
          {getWorkflowIcon(action.workflow)}
        </div>

        {/* Action Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900">{action.label}</span>
            <Badge color={getPriorityColor(action.priority)} size="xs">
              {action.priority}
            </Badge>
          </div>
          <p className="text-sm text-gray-600 mb-2">{action.description}</p>
          {action.repository && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <FaGithub className="w-3 h-3" />
              <span>{action.repository}</span>
            </div>
          )}
          {action.estimated_time_minutes && (
            <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
              <HiClock className="w-3 h-3" />
              <span>~{action.estimated_time_minutes} min</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex-shrink-0 flex flex-col gap-2">
          <Button
            size="xs"
            color={action.priority === 'high' ? 'failure' : action.priority === 'medium' ? 'warning' : 'info'}
            onClick={() => handleTriggerAction(action)}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <Spinner size="sm" className="mr-1" />
            ) : (
              <HiPlay className="w-3 h-3 mr-1" />
            )}
            Run
          </Button>
          {onDismissAction && (
            <Button
              size="xs"
              color="gray"
              onClick={() => onDismissAction(action.action_id)}
              disabled={isProcessing}
            >
              Dismiss
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Render priority section
  const renderPrioritySection = (
    priority: 'high' | 'medium' | 'low',
    sectionActions: RecommendedAction[]
  ) => {
    if (sectionActions.length === 0) return null;

    return (
      <div className="mb-4 last:mb-0">
        <div className="flex items-center gap-2 mb-2">
          <Badge color={getPriorityColor(priority)}>
            {getPriorityLabel(priority)}
          </Badge>
          <span className="text-sm text-gray-500">
            ({sectionActions.length} {sectionActions.length === 1 ? 'action' : 'actions'})
          </span>
        </div>
        <div className="space-y-2">
          {sectionActions.map(renderActionItem)}
        </div>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <HiLightBulb className="w-5 h-5 text-yellow-500" />
            Recommended Actions
          </h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  // Empty state
  if (totalActions === 0) {
    return (
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <HiLightBulb className="w-5 h-5 text-yellow-500" />
            Recommended Actions
          </h3>
          {onRefresh && (
            <Button size="xs" color="gray" onClick={onRefresh}>
              <HiRefresh className="w-4 h-4" />
            </Button>
          )}
        </div>
        <div className="text-center py-8">
          <HiCheckCircle className="w-12 h-12 mx-auto text-green-400 mb-2" />
          <p className="text-gray-600">No recommended actions at this time</p>
          <p className="text-sm text-gray-500 mt-1">
            Your repositories and specifications are looking healthy!
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <HiLightBulb className="w-5 h-5 text-yellow-500" />
          Recommended Actions
          {highPriorityCount > 0 && (
            <Badge color="failure">{highPriorityCount} urgent</Badge>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {onRefresh && (
            <Button size="xs" color="gray" onClick={onRefresh}>
              <HiRefresh className="w-4 h-4" />
            </Button>
          )}
          <span className="text-sm text-gray-500">
            {totalActions} {totalActions === 1 ? 'action' : 'actions'}
          </span>
        </div>
      </div>

      {/* Summary Stats */}
      {totalActions > 3 && (
        <div className="flex gap-4 mb-4 pb-4 border-b">
          {groupedActions.high.length > 0 && (
            <div className="flex items-center gap-2">
              <Badge color="failure">{groupedActions.high.length}</Badge>
              <span className="text-sm text-gray-600">High</span>
            </div>
          )}
          {groupedActions.medium.length > 0 && (
            <div className="flex items-center gap-2">
              <Badge color="warning">{groupedActions.medium.length}</Badge>
              <span className="text-sm text-gray-600">Medium</span>
            </div>
          )}
          {groupedActions.low.length > 0 && (
            <div className="flex items-center gap-2">
              <Badge color="info">{groupedActions.low.length}</Badge>
              <span className="text-sm text-gray-600">Low</span>
            </div>
          )}
        </div>
      )}

      {/* Actions List - Grouped by Priority */}
      <div className="space-y-4">
        {renderPrioritySection('high', expanded ? groupedActions.high : groupedActions.high.slice(0, 3))}
        {renderPrioritySection('medium', expanded ? groupedActions.medium : groupedActions.medium.slice(0, expanded ? undefined : 3))}
        {renderPrioritySection('low', expanded ? groupedActions.low : groupedActions.low.slice(0, expanded ? undefined : 2))}
      </div>

      {/* Expand/Collapse Button */}
      {totalActions > maxVisible && (
        <div className="mt-4 pt-4 border-t text-center">
          <Button
            size="sm"
            color="gray"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <>
                <HiChevronUp className="w-4 h-4 mr-1" />
                Show Less
              </>
            ) : (
              <>
                <HiChevronDown className="w-4 h-4 mr-1" />
                Show All ({totalActions - maxVisible} more)
              </>
            )}
          </Button>
        </div>
      )}
    </Card>
  );
};

export default RecommendedActionsPanel;
